import { Autocomplete, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import GenericModal from "components/GenericModal";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { updateAttachmentType } from "api/applicants/updateAttachmentType";
import { DOCUMENT_TYPES } from "utils/attachmentHelpers";
import CustomTextField from "components/Form/CustomTextField";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";

const ChangeAttachmentTypeModal = ({ file, open, setOpen }) => {
  const { currentApplicant, setCurrentApplicant } = useAppContextController();
  const [selectedType, setSelectedType] = useState(file.type || "");
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const isEmployee = currentApplicant?.status === "Employee";

  const { dropdowns: attachmentTypes, isLoadingDropdown } = useDropdowns({
    entity: "attachmentTypes",
  });

  const updateAttachmentTypeMutation = useMutation(updateAttachmentType, {
    onSuccess: async (result) => {
      console.log("Attachment type updated successfully:", result);
      enqueueSnackbar("Attachment type updated successfully!", { variant: "success" });      
      // Update the current applicant with the returned updatedApplicant
      if (result.updatedApplicant) {
        setCurrentApplicant(result.updatedApplicant);
      }
      
      await queryClient.invalidateQueries(isEmployee ? "employee_table" : "applicants");
    },
    onError: (error) => {
      console.error("Error updating attachment type:", error);
      enqueueSnackbar(`Error updating attachment type: ${error.message}`, { variant: "error" });
    },
    mutationKey: "updateAttachmentTypeMutation",
  });

  const handleClose = () => {
    setSelectedType(file.type);
    setOpen(false);
  };

  const documentTypeOptions = [
    ...Object.values(DOCUMENT_TYPES),
    ...(attachmentTypes?.filter(type => type !== "Onboarding_Documents") || [])
  ];

  const validateSelectedType = () => {
    if (!selectedType || selectedType.trim() === "") {
      enqueueSnackbar("Please select a valid document type", { variant: "error" });
      return false;
    }
    if (!documentTypeOptions.includes(selectedType)) {
      enqueueSnackbar("Please select a valid document type from the list", { variant: "error" });
      return false;
    }
    return true;
  };

  const handleTypeChange = () => {
    if (selectedType === file.type) {
      setOpen(false);
      return;
    }

    if (!validateSelectedType()) {
      return;
    }

    const { name, recognitionDate, ...attachment } = file;

    updateAttachmentTypeMutation.mutate(
      {
        id: currentApplicant._id,
        data: {
          attachment,
          newType: selectedType,
        },
      },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

  const header = (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box sx={{ mt: 2, ml: 0.5 }}>
        <Typography variant="h5" color="dark">
          Change Attachment Type
        </Typography>
      </Box>
      <IconButton onClick={handleClose}>
        <CloseIcon />
      </IconButton>
    </Box>
  );

  const modalBody = (
    <Stack spacing={2}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Current type: <strong style={{ color: "#1976d2" }}>{file.type}</strong>
      </Typography>

      <Autocomplete
        options={documentTypeOptions}
        value={selectedType}
        onChange={(e, newValue) => setSelectedType(newValue || file.type)}
        freeSolo={false}
        loading={isLoadingDropdown}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            variant="outlined"
            label={isLoadingDropdown ? "Loading attachment types..." : "Select New Type"}
            sx={{
              "& .MuiInputBase-root": {
                py: "5px",
              },
            }}
          />
        )}
      />
    </Stack>
  );

  const modalButtons = (
    <Stack direction="row" spacing={2}>
      <Button onClick={handleClose} disabled={updateAttachmentTypeMutation.isLoading}>
        Cancel
      </Button>
      <Button
        onClick={handleTypeChange}
        variant="contained"
        disabled={updateAttachmentTypeMutation.isLoading || selectedType === file.type}
      >
        {updateAttachmentTypeMutation.isLoading ? <CircularProgress size={20} /> : "Update Type"}
      </Button>
    </Stack>
  );

  return (
    <GenericModal
      open={open}
      setOpen={setOpen}
      header={header}
      body={modalBody}
      buttons={modalButtons}
      width="30%"
      overflow="auto"
    />
  );
};

export default ChangeAttachmentTypeModal;
